// moveoutFundsForm — DEPENDENCY-FREE pure helpers for the WEL-48 Move-Out Funds
// Direction Agreement intake form. No network / no `@/` imports so it stays
// unit-testable in isolation (mirrors lib/intakeAnimalsLabel.ts). Imported by
// both lib/moveoutFundsClient.ts (which re-exports for app call sites) and the
// MoveoutFundsIntakeFormPage.

/** The body POSTed to submit the intake. Mirrors backend MoveoutFundsIntake. */
export interface MoveoutFundsIntakeSubmission {
  recipientKind: 'resident' | 'cosigner' | 'other';
  /** Required when recipientKind === 'resident'. */
  recipientResidentTenantId?: string;
  /** Required when recipientKind === 'cosigner' (WEL-143). */
  recipientCosignerTenantId?: string;
  /** Required when recipientKind === 'other'. */
  otherName?: string;
  /** The Designated Recipient's forwarding mailing address (required, multi-line ok). */
  forwardingAddress: string;
}

/** The raw form state the intake page collects before building a submission. */
export interface MoveoutFundsFormDraft {
  /** A resident tenantId, the OTHER sentinel, or '' (nothing picked yet). */
  choice: string;
  otherName: string;
  /** LEGACY — no longer collected or sent (WEL-143); kept so existing callers
   *  constructing a draft still typecheck. */
  otherEmail?: string;
  forwardingAddress: string;
}

/** Sentinel `choice` value for the third-party "Other" recipient path. */
export const MOVEOUT_OTHER_CHOICE = '__other__';

/**
 * Prefix marking a `choice` as a CO-SIGNER's tenant id (WEL-143).
 *
 * Residents and co-signers are separate rosters that both key by AppFolio tenant
 * id, and the server validates the pick against the matching roster — so the
 * choice has to carry which roster it came from, not just the id.
 */
export const MOVEOUT_COSIGNER_PREFIX = 'cosigner:';

/** The `choice` value for a co-signer option. */
export function cosignerChoice(tenantId: string): string {
  return `${MOVEOUT_COSIGNER_PREFIX}${tenantId}`;
}

/** True when `choice` names a co-signer. */
export function isCosignerChoice(choice: string): boolean {
  return choice.startsWith(MOVEOUT_COSIGNER_PREFIX);
}

/** True when the draft is complete + valid enough to submit. */
export function canSubmitMoveoutFunds(d: MoveoutFundsFormDraft): boolean {
  if (!d.choice) return false;
  if (isCosignerChoice(d.choice) && !d.choice.slice(MOVEOUT_COSIGNER_PREFIX.length)) {
    return false;
  }
  if (!d.forwardingAddress.trim()) return false;
  if (d.choice === MOVEOUT_OTHER_CHOICE) {
    if (!d.otherName.trim()) return false;
  }
  return true;
}

/**
 * Build the wire submission from a (valid) draft. Returns null when the draft is
 * incomplete/invalid so callers can't accidentally POST a half-filled body.
 */
export function buildMoveoutFundsSubmission(
  d: MoveoutFundsFormDraft,
): MoveoutFundsIntakeSubmission | null {
  if (!canSubmitMoveoutFunds(d)) return null;
  if (d.choice === MOVEOUT_OTHER_CHOICE) {
    return {
      recipientKind: 'other',
      otherName: d.otherName.trim(),
      forwardingAddress: d.forwardingAddress.trim(),
    };
  }
  if (isCosignerChoice(d.choice)) {
    return {
      recipientKind: 'cosigner',
      recipientCosignerTenantId: d.choice.slice(MOVEOUT_COSIGNER_PREFIX.length),
      forwardingAddress: d.forwardingAddress.trim(),
    };
  }
  return {
    recipientKind: 'resident',
    recipientResidentTenantId: d.choice,
    forwardingAddress: d.forwardingAddress.trim(),
  };
}
